<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { isNoShowRisk } from '@/domain/reservations/reservation-status';
  import {
    formatDateLong,
    formatTime,
  } from '@/domain/reservations/reservation-time';
  import type { IReservation } from '@/interfaces/IReservation';
  import V2ReservationDepositBadge from './V2ReservationDepositBadge.vue';
  import V2ReservationSourceBadge from './V2ReservationSourceBadge.vue';
  import V2ReservationStatusBadge from './V2ReservationStatusBadge.vue';
  const props = defineProps<{
    reservation: IReservation;
    timeZone: string;
    lateMinutes: number;
  }>();
  const emit = defineEmits<{ (event: 'close'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const schedule = computed(
    () =>
      `${formatTime(
        props.reservation.startsAt,
        props.timeZone,
        proxy.$i18n.locale
      )} · ${formatDateLong(
        props.reservation.startsAt,
        props.timeZone,
        proxy.$i18n.locale
      )}`
  );
</script>

<template>
  <header class="reservation-detail-header">
    <div class="identity-row">
      <div class="identity">
        <span class="booking-code">{{ reservation.code }}</span>
        <h2>
          {{ reservation.guestName || $t('reservations.common.anonymous') }}
        </h2>
      </div>
      <PosUiButton
        color="neutral"
        variant="ghost"
        icon="close"
        :aria-label="$t('close')"
        @click="emit('close')"
      />
    </div>
    <p class="party">
      {{
        $tc('reservations.common.people', reservation.partySize, {
          count: reservation.partySize,
        })
      }}
      · {{ schedule }}
    </p>
    <div class="badges">
      <PosUiBadge
        v-if="isNoShowRisk(reservation.customer)"
        color="error"
        variant="soft"
        :title="
          $t('reservations.detail.noShowCount', {
            count: reservation.customer?.noShowsCount,
          })
        "
        >{{ $t('reservations.detail.noShowRisk') }}</PosUiBadge
      >
      <PosUiBadge
        v-if="reservation.guestLateMinutes"
        color="warning"
        variant="soft"
      >
        {{
          $t('reservations.detail.runningLate', {
            minutes: reservation.guestLateMinutes,
          })
        }}
      </PosUiBadge>
      <V2ReservationStatusBadge
        :status="reservation.status"
        :late-minutes="lateMinutes"
        :auto-closed="reservation.autoClosed"
      /><V2ReservationSourceBadge
        :source="reservation.source"
      /><V2ReservationDepositBadge
        :status="reservation.depositStatus"
        :amount="reservation.depositAmount"
      /><PosUiBadge
        v-if="reservation.customer?.isClubMember"
        color="neutral"
        variant="soft"
        icon="star-outline"
        >{{ $t('reservations.club.member') }}</PosUiBadge
      >
    </div>
  </header>
</template>

<style scoped lang="scss">
  .reservation-detail-header {
    @apply tw-flex tw-flex-col tw-gap-3 tw-border-b tw-border-neutral-200 tw-p-5;
    .identity-row {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3;
    }
    .identity {
      @apply tw-min-w-0;
    }
    .booking-code {
      @apply tw-text-xs tw-font-medium tw-tracking-wider tw-text-neutral-400;
    }
    h2 {
      @apply tw-break-words tw-text-xl tw-font-semibold tw-leading-tight tw-text-neutral-900;
    }
    .party {
      @apply tw-text-sm tw-text-neutral-500;
    }
    .badges {
      @apply tw-flex tw-flex-wrap tw-gap-1.5;
    }
  }
</style>
